import { SDN } from 'utils/constants';

export type ResponseOfferUser = {
	enabled?: boolean;
	id: number;
	// MESSENGER_BETA - It could be deleted after enabling messenger for all users.
	messenger_testing: boolean;
	portrait_url: string;
	ubox_created_date?: string;
	user_service: {
		created_date?: string;
		service_enabled?: boolean;
		service_name?: string;
		shop_description?: string;
		shop_name?: string;
		shop_url: string;
	};
	verification?: Verification;
};

export type Verification = 'verified' | 'unverified' | 'expired';

export function normalizeOfferUser(user: ResponseOfferUser): UserData {
	const portraitUrl = user.portrait_url;
	const isFromSdn = portraitUrl && portraitUrl.includes('.sdn.');
	const transformedPortraitUrl = isFromSdn ? `${portraitUrl}${SDN.w280WebP}` : portraitUrl;

	return {
		enabled: user.enabled,
		id: user.id,
		inDb: true,
		messengerTesting: user.messenger_testing,
		portraitUrl: transformedPortraitUrl,
		uboxCreatedDate: user.ubox_created_date,
		userService: {
			createdDate: user.user_service.created_date,
			serviceEnabled: user.user_service.service_enabled,
			serviceName: user.user_service.service_name,
			shopDescription: user.user_service.shop_description,
			shopName: user.user_service.shop_name,
			shopUrl: user.user_service.shop_url,
		},
		verification: user.verification,
	};
}

export type ResponseUserData = {
	adverts_num: number;
	display_name: undefined | string;
	emails: { email: string; is_deleted?: boolean | undefined; verified?: boolean | undefined }[];
	enabled?: undefined | boolean;
	id: number;
	in_db: boolean;
	login: string;
	name: string;
	phones: { default: boolean; is_deleted?: boolean; phone: string; verified?: boolean }[];
	portrait_from_profile: boolean;
	portrait_url: string;
	portrait_visible: boolean;
	ubox_created_date: string;
	user_service: {
		created_date: string;
		is_advertiser: boolean;
		service_enabled: boolean;
		service_name: string;
		shop_description: string;
		shop_name?: undefined | string;
		shop_url: string;
		vop_accepted: boolean;
		vouchers: number;
	};
	verification: undefined | Verification;
	settings?: {
		listing_row_view: boolean;
	};
};

export type UserData = {
	advertsNum?: number;
	displayName?: string;
	emails?: { email: string; isDeleted: boolean; verified: boolean }[];
	enabled: undefined | boolean;
	id: number;
	inDb: boolean;
	login?: string;
	messengerTesting?: boolean;
	name?: string;
	phones?: { isDefault: boolean; isDeleted: boolean; phone: string; verified: boolean }[];
	portraitFromProfile?: boolean;
	portraitUrl: string;
	portraitVisible?: boolean;
	uboxCreatedDate?: string;
	userService: {
		createdDate?: string;
		isAdvertiser?: boolean;
		serviceEnabled?: boolean;
		serviceName?: string;
		shopDescription?: string;
		shopName: undefined | string;
		shopUrl: string;
		vopAccepted?: boolean;
		vouchers?: number;
	};
	verification: undefined | Verification;
	isPremise?: false | undefined;
	settings?: {
		listingRowView: boolean;
	};
};

export function normalizeUserData(user: ResponseUserData): UserData {
	const portraitUrl = user.portrait_url;
	const isFromSdn = portraitUrl && portraitUrl.includes('.sdn.');
	const transformedPortraitUrl = isFromSdn ? `${portraitUrl}${SDN.w280WebP}` : portraitUrl;

	return {
		advertsNum: user.adverts_num,
		displayName: user.display_name,
		emails: user.emails?.map(({ email, is_deleted, verified }) => ({
			email,
			isDeleted: is_deleted ?? false,
			verified: verified ?? false,
		})),
		enabled: user.enabled,
		id: user.id,
		inDb: user.in_db,
		login: user.login,
		name: user.name,
		phones: user.phones?.map(({ default: isDefault, is_deleted, phone, verified }) => ({
			isDefault: isDefault ?? false,
			isDeleted: is_deleted ?? false,
			phone,
			verified: verified ?? false,
		})),
		portraitFromProfile: user.portrait_from_profile,
		portraitUrl: transformedPortraitUrl,
		portraitVisible: user.portrait_visible,
		uboxCreatedDate: user.ubox_created_date,
		userService: {
			createdDate: user.user_service.created_date,
			isAdvertiser: user.user_service.is_advertiser,
			serviceEnabled: user.user_service.service_enabled,
			serviceName: user.user_service.service_name,
			shopDescription: user.user_service.shop_description,
			shopName: user.user_service.shop_name,
			shopUrl: user.user_service.shop_url,
			vopAccepted: user.user_service.vop_accepted,
			vouchers: user.user_service.vouchers,
		},
		verification: user.verification,
		settings: {
			listingRowView: user.settings?.listing_row_view ?? false,
		},
	};
}
